import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  LayoutDashboard,
  Users,
  Handshake,
  ClipboardList,
  Blocks,
  MessageSquare,
} from 'lucide-react'

import { AnimatedTabs, type AnimatedTab } from '@/components/customers/animated-tabs'
import { Button } from '@/components/ui/button'
import { DashboardTab } from '@/features/customers/components/dashboard-tab'
import { CustomersTab } from '@/features/customers/components/customers-tab'
import { DealsTab } from '@/features/customers/components/deals-tab'
import { AddOnsCatalogTab } from '@/features/customers/components/addons-catalog-tab'
import { RegistrationsTab } from '@/features/documents/components/registrations-tab'
import { MessagesSidebar } from '@/features/messaging/components/messages-sidebar'
import { requireFlow } from '@/features/auth/guards'

const CUSTOMERS_TABS = ['dashboard', 'customers', 'deals', 'addons', 'registrations'] as const
type CustomerTab = (typeof CUSTOMERS_TABS)[number]

function isCustomerTab(value: unknown): value is CustomerTab {
  return typeof value === 'string' && (CUSTOMERS_TABS as readonly string[]).includes(value)
}

export const Route = createFileRoute('/_authenticated/sales/')({
  beforeLoad: requireFlow('sales'),
  // q searches the Customers tab, deal_q the Deals tab, addon_q the Add-Ons
  // tab — separate params so a search on one tab never filters the others.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: CustomerTab; q?: string; deal_q?: string; addon_q?: string } => {
    return {
      tab: isCustomerTab(search.tab) ? search.tab : undefined,
      q: typeof search.q === 'string' ? search.q : undefined,
      deal_q: typeof search.deal_q === 'string' ? search.deal_q : undefined,
      addon_q: typeof search.addon_q === 'string' ? search.addon_q : undefined,
    }
  },
  component: CustomersPage,
})

function CustomersPage() {
  const { tab, q, deal_q: dealQ, addon_q: addonQ } = Route.useSearch()
  const navigate = useNavigate({ from: '/sales/' })
  const activeTab: CustomerTab = tab ?? 'dashboard'
  const [messagesOpen, setMessagesOpen] = useState(false)

  const setActiveTab = (next: string) => {
    if (!isCustomerTab(next)) return
    navigate({
      // Dashboard keeps the other tabs' search params (minus the tab
      // marker) so bouncing between tabs never discards a typed search.
      search: () =>
        next === 'dashboard'
          ? { q, deal_q: dealQ, addon_q: addonQ }
          : { tab: next, q, deal_q: dealQ, addon_q: addonQ },
    })
  }

  const setSearchQ = (newQ: string) => {
    navigate({ search: (prev) => ({ ...prev, q: newQ || undefined }), replace: true })
  }

  const setDealQ = (newQ: string) => {
    navigate({ search: (prev) => ({ ...prev, deal_q: newQ || undefined }), replace: true })
  }

  const setAddonQ = (newQ: string) => {
    navigate({ search: (prev) => ({ ...prev, addon_q: newQ || undefined }), replace: true })
  }

  const tabs: AnimatedTab[] = [
    {
      value: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      content: <DashboardTab />,
    },
    {
      value: 'customers',
      label: 'Customers',
      icon: Users,
      content: <CustomersTab q={q} onQChange={setSearchQ} />,
    },
    {
      value: 'deals',
      label: 'Deals',
      icon: Handshake,
      content: <DealsTab q={dealQ} onQChange={setDealQ} />,
    },
    {
      value: 'addons',
      label: 'Add-Ons',
      icon: Blocks,
      content: <AddOnsCatalogTab q={addonQ} onQChange={setAddonQ} />,
    },
    {
      value: 'registrations',
      label: 'Registrations',
      icon: ClipboardList,
      content: <RegistrationsTab />,
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Users className="h-8 w-8" /> Sales
          </h1>
          <p className="text-muted-foreground">
            Manage customers, track leads, and grow your pipeline.
          </p>
        </div>
        <Button variant="outline" className="shrink-0" onClick={() => setMessagesOpen(true)}>
          <MessageSquare className="mr-2 h-4 w-4" /> Messages
        </Button>
      </div>

      <AnimatedTabs tabs={tabs} value={activeTab} onValueChange={setActiveTab} />

      <MessagesSidebar open={messagesOpen} onOpenChange={setMessagesOpen} />
    </div>
  )
}
